import * as Plot from '@observablehq/plot';
import { useTooltipStore } from 'hooks/useTooltipStore';
import { useEffect, useRef, useState } from 'react';

interface MemrePlotProps {
  data: any;
  aspectRatio?: number;
  tooltipFunction: (key: string) => React.ReactNode;
  onClick?: (event: Event) => void;
  ariaLabel?: string;
  ariaDescription?: string;
}

export default function MemrePlot({
  data,
  aspectRatio = 2,
  tooltipFunction,
  onClick,
  ariaLabel,
  ariaDescription,
}: MemrePlotProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<HTMLDivElement>(null);

  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const newWidth = containerRef.current?.clientWidth || 0;
    setWidth(newWidth);
    setHeight(newWidth / aspectRatio);
  }, [containerRef.current?.clientWidth, aspectRatio]);

  const showTooltip = useTooltipStore((state) => state.showTooltip);
  const hideTooltip = useTooltipStore((state) => state.hideTooltip);

  useEffect(() => {
    if (!data) {
      return;
    }
    const chart = Plot.plot({ ...data, width, height });

    const handleMouseMove = (event: any) => {
      const targetElement = event.target as HTMLElement;
      const tooltipKey = targetElement.getAttribute('aria-label');

      if (tooltipKey) {
        const rect = targetElement.getBoundingClientRect();
        const x = rect.left + rect.width / 2;
        const y = rect.top + rect.height / 2;

        const restraints = { bounds: { x, y } };

        showTooltip({ content: tooltipFunction(tooltipKey), restraints });
      } else {
        hideTooltip();
      }
    };

    if (chartRef.current) {
      chartRef.current?.appendChild(chart);

      // Accessibility: give the generated chart SVG an accessible name and
      // description so screen readers convey the chart (audit #47 / MEM-623).
      // Keyboard/hover access to data points (#48) is deferred.
      const svg = (
        chart.tagName?.toLowerCase() === 'svg' ? chart : chart.querySelector('svg')
      ) as SVGSVGElement | null;
      if (svg) {
        if (ariaLabel) {
          svg.setAttribute('role', 'img');
          svg.setAttribute('aria-label', ariaLabel);
        }
        if (ariaDescription) {
          const descEl = document.createElementNS('http://www.w3.org/2000/svg', 'desc');
          descEl.textContent = ariaDescription;
          svg.insertBefore(descEl, svg.firstChild);
        }
      }

      const elements = chart.querySelectorAll('[title]');
      elements.forEach((el) => {
        const title = el.getAttribute('title');
        if (title) {
          el.setAttribute('data-memre-tooltip', title);
        }
      });

      chart.addEventListener('mousemove', handleMouseMove);
      chart.addEventListener('mouseout', hideTooltip);
      if (onClick) {
        chart.addEventListener('click', onClick);
      }
    }

    return () => {
      chart.removeEventListener('mousemove', handleMouseMove);
      chart.removeEventListener('mouseout', hideTooltip);
      if (onClick) {
        chart.removeEventListener('click', onClick);
      }
      chart.remove();
    };
  }, [data, onClick, tooltipFunction, width, height, hideTooltip, showTooltip, ariaLabel, ariaDescription]);

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
      }}
    >
      <div ref={chartRef} />
    </div>
  );
}
